import { withNamespaces } from "react-i18next";
import { RiInformationLine } from "react-icons/ri";
import { Badge, UncontrolledTooltip } from "reactstrap";
import { KYBMonitoringAlertsStatuses, KYBPEPTierValues, KYBScreeningDataSets } from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils"
import  { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';

const genders = {
    'male': 'Male',
    'female': 'Female'
}

const MatchMonitoringResultDetails = ({ t, monitoringDetailQuery }) => {
    const {
        data: resultData
    } = monitoringDetailQuery;

    const dateUtils = new DateUtils();

    return (
        <div className="sanction-table-container mb-4 bg-white">
            <table className="table table-striped">
                <tbody>
                    <tr>
                        <th>
                            {t('Name')}
                            <span
                                id="match-details-name-tooltip"
                                className="ms-1"
                                style={{ cursor: "pointer" }}>
                                <RiInformationLine />
                            </span>
                            <UncontrolledTooltip
                                target="match-details-name-tooltip"
                            >
                                {t("The primary name on the profile.")}
                            </UncontrolledTooltip>
                        </th>
                        <td>
                            {resultData.full_name}
                        </td>
                    </tr>

                    <tr>
                        <th>
                            {t('Match Name')}
                            <span
                                id="match-details-match-name-tooltip"
                                className="ms-1"
                                style={{ cursor: "pointer" }}>
                                <RiInformationLine />
                            </span>
                            <UncontrolledTooltip
                                target="match-details-match-name-tooltip"
                            >
                                {t("The name on the profile that has been matched in the search.")}
                            </UncontrolledTooltip>
                        </th>
                        <td>
                            {resultData.match_name}
                        </td>
                    </tr>

                    <tr>
                        <th>
                            {t('Datasets')}
                            <span
                                id="datasets-tooltip"
                                className="ms-1"
                                style={{ cursor: "pointer" }}>
                                <RiInformationLine />
                            </span>
                            <UncontrolledTooltip
                                target="datasets-tooltip"
                            >
                                {t("The datasets in which the profile has been found.")}
                            </UncontrolledTooltip>
                        </th>

                        <td>
                            {(resultData?.datasets || []).map((datasetKey, index) => {
                                return (
                                    <Badge
                                        key={index}
                                        id={`match-details-dataset-${datasetKey}`}
                                        className="bg-info ms-1 me-1"
                                    >
                                        {t(KYBScreeningDataSets[datasetKey]?.title)}

                                        <UncontrolledTooltip
                                            target={`match-details-dataset-${datasetKey}`}
                                        >
                                            {t(KYBScreeningDataSets[datasetKey]?.description)}
                                        </UncontrolledTooltip>
                                    </Badge>
                                )
                            })}
                        </td>
                    </tr>

                    <tr>
                        <th>
                            {t('PEP Tier')}
                        </th>
                        <td>
                            {t(KYBPEPTierValues?.[resultData.pep_tier]?.title ?? "Not provided")}

                            <span
                                id="match-details-pep-tier-tooltip"
                                className="ms-1"
                                style={{ cursor: "pointer" }}>
                                <RiInformationLine />
                            </span>
                            <UncontrolledTooltip
                                target="match-details-pep-tier-tooltip"
                            >
                                {t("Defines the PEP tier of the matched person. Where PEP Tier 1 indicates senior roles, PEP Tier 2 - middle-ranking, and PEP Tier 3 - junior officials.")}
                            </UncontrolledTooltip>
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Match Status')}</th>
                        <td>
                            {t(KYBMonitoringAlertsStatuses?.[resultData.current_status]?.title)}

                            {KYBMonitoringAlertsStatuses?.[resultData.current_status]?.description ? (
                                <>
                                    <span
                                        id="match-details-match-status-desc-tooltip"
                                        className="ms-1"
                                        style={{ cursor: "pointer" }}>
                                        <RiInformationLine />
                                    </span>
                                    <UncontrolledTooltip
                                        target="match-details-match-status-desc-tooltip"
                                    >
                                        {t(KYBMonitoringAlertsStatuses[resultData.current_status].description)}
                                    </UncontrolledTooltip>
                                </>
                            ) : null}
                        </td>
                    </tr>
                    <tr>
                        <th>
                            {t('Score')}
                            <span
                                id="match-details-score-tooltip"
                                className="ms-1"
                                style={{ cursor: "pointer" }}>
                                <RiInformationLine />
                            </span>
                            <UncontrolledTooltip
                                target="match-details-score-tooltip"
                            >
                                {t("The match score of the profile.")}
                            </UncontrolledTooltip>
                        </th>
                        <td>
                            <p>
                                {resultData.score}{' '}{t('(Min: 50, Max: 100)')}
                            </p>
                        </td>
                    </tr>

                    <tr>
                        <th>{t('Dates of Birth')}</th>
                        <td>
                            {(resultData?.dates_of_birth || []).length > 0 ? (
                                resultData.dates_of_birth.map((birthDate, index) => {
                                    return (
                                        <p
                                            key={index}
                                            className="mb-1"
                                        >
                                            {dateUtils.convertDateToDate(
                                                birthDate,
                                                INTERNATIONAL_DATE_FORMAT
                                            )}
                                        </p>
                                    )
                                })
                            ) : (
                                <span className="text-muted">---</span>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Gender')}</th>
                        <td>
                            <p>
                                {t(
                                    genders[resultData?.gender?.toLowerCase()] || 'Unknown'
                                )}
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Countries')}</th>
                        <td>
                            <p>
                                {(resultData?.countries || []).join(', ')}
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <th>{t('Version')}</th>
                        <td>
                            <p>
                                {resultData.version}
                            </p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

export default withNamespaces()(
    MatchMonitoringResultDetails
);